import type { LatLngLiteral } from "./route-geometry";

/**
 * Where a dispatch centre is, from whatever shape its address carries.
 *
 * `address.location` is typed `unknown` — it is stored as the geocoder left it,
 * and across the fleet that has meant a plain `{lat,lng}`, a GeoJSON point
 * (which puts LONGITUDE first), and occasionally a `"lat,lng"` string.
 *
 * A centre whose location cannot be read gets no marker. Guessing one would
 * put a building on a map at a place it is not, which is worse than an absence
 * a dispatcher can see.
 */
export function centerLatLng(center: {
  address?: { location?: unknown } | null;
}): LatLngLiteral | null {
  const location = center.address?.location;
  if (!location) return null;

  if (typeof location === "string") {
    const [lat, lng] = location.split(",").map(Number);
    return usable(lat, lng);
  }

  if (typeof location !== "object") return null;

  const point = location as {
    lat?: unknown;
    lng?: unknown;
    coordinates?: unknown;
  };

  if (typeof point.lat === "number" && typeof point.lng === "number") {
    return usable(point.lat, point.lng);
  }

  // GeoJSON is [longitude, latitude] — the reverse of every other shape here,
  // and silently swapping them drops a Toronto centre into the Indian Ocean.
  if (Array.isArray(point.coordinates) && point.coordinates.length >= 2) {
    const [lng, lat] = point.coordinates;
    if (typeof lat === "number" && typeof lng === "number") {
      return usable(lat, lng);
    }
  }

  return null;
}

function usable(lat: number, lng: number): LatLngLiteral | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  // 0,0 is in the Gulf of Guinea and is what an unset pair looks like.
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}
